import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { ProOnly } from '../label/ProOnly';
import { useClasses } from '../hooks';
import { NekoSpinner } from '../misc/Spinner';

import { Icon } from '@iconify/react';
import checkboxBlankCircleOutline from '@iconify/icons-mdi/checkbox-blank-circle-outline';
import recordCircle from '@iconify/icons-mdi/record-circle';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';
import { NekoIcon } from '../misc/Icon';
import { NekoInput } from './Input';
import NekoUI from '../NekoUI';
import { NekoContextMenu } from '../misc/ContextMenu';

const Select = Styled.div`
  border-radius: 8px;
  user-select: none;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease;

  .neko-select-option-label {
    overflow: hidden;
    height: 100%;
    display: flex;
    align-items: center;
  }

  &.show-options {
    border-radius: 8px 8px 0 0;
  }

  &[data-is-disabled=true], &.disabled {
    cursor: none;
    pointer-events: none;
    color: var(--neko-dark-gray);

    .neko-select-option {
      pointer-events: none;
      background: var(--neko-gray);
      border-color: var(--neko-gray);
    }
  }
`;

const SelectedOption = Styled.div`
  align-items: center;
  background-color: var(--neko-input-background);
  border: 1.5px solid var(--neko-input-border);
  border-radius: 8px;
  display: flex;
  font-family: ${Theme.fontFamily};
  font-size: 14px;
  padding: 0 5px 0 10px;
  box-sizing: border-box;
  height: 30px;

  &.isBusy {
    padding-left: 5px;
  }

  .rightContent {
    align-items: center;
    display: flex;
    margin-left: auto;
  }
`;

const Description = Styled.div`
  display: block;
  margin-top: 5px;
  font-size: ${Theme.fontSizeSmall};
  line-height: 14px;
  color: var(--neko-dark-gray);

  * {
    font-size: ${Theme.fontSizeSmall};
    line-height: inherit;
    margin: 0;
  }
`;

const Options = Styled.div`
  border-radius: 8px;
  overflow: hidden;
  position: absolute;
  top: 0;
  width: 100%;

  &.hidden {
    opacity: 0;
  }
`;

const OptionsContainer = Styled.div`
  overflow-y: auto;
  max-height: 300px;
  background-color: var(--block-background-alternative);

  &.neko-select-filter-container {
    background-color: var(--block-background-alternative);
    padding: 6px;
    margin-top: 2px;
    overflow: hidden;

    div {
      overflow: hidden;
    }
  }
`;

const RadioOption = Styled.div`
  font-family: ${Theme.fontFamily};
  margin-bottom: 6px;

  input {
    display: none;
  }

  label {
    cursor: pointer;
    display: flex;
  }

  .inner-container {
    margin-left: 4px;

    .label {
      display: block;
      font-size: 14px;
      line-height: 17px;
      padding-top: 4.5px;
      padding-bottom: 4px;
    }
    .description {
      display: block;
      font-size: ${Theme.fontSizeSmall};
    }
  }

  &.disabled {
    color: var(--neko-disabled-form);

    label {
      cursor: default;
    }
  }
`;

const SelectOption = Styled.div`
  background-color: var(--block-background-alternative);
  cursor: pointer;
  font-family: ${Theme.fontFamily};
  padding: 6px 12px;

  &:hover {
    filter: brightness(80%);
  }

  input {
    display: none;
  }

  .option {
    align-items: center;
    color: var(--neko-white);
    display: flex;
    justify-content: space-between;
    font-family: ${Theme.fontFamily};
    font-size: 13px;
    line-height: 17px;

    .option-group {
      align-items: center;
      display: flex;
    }
  }

  &.disabled {
    background-color: rgb(224 156 54);
    pointer-events: none;

    .option {
      color: rgb(255 255 255 / 35%);
    }
  }
`;

const StyledNekoSelect = props => {
  const { name, value, scrolldown = false, isPro = false, onChange, className, disabled, isBusy = false,
    chevronIconSize = 24, textFiltering, ...rest } = props;
  const currentOption = React.Children.toArray(props.children)
    .find(child => child.props.value === value || child.props.checked)?.props;
  const label = currentOption?.label || 'Select';
  const description = currentOption?.description || props.description;
  const requirePro = currentOption?.requirePro || false;
  const icon = currentOption?.icon;
  const iconSize = currentOption?.iconSize;
  const [ showOptions, toggleOptions ] = useState(false);
  const [ filterText, setFilterText ] = useState('');
  const componentRef = useRef();
  const showUpProOnly = requirePro && !isPro;

  const onContextMenuClose = () => {
    if (showOptions) {
      toggleOptions(false);
    }
  }

  const onOptionClick = (newValue) => {
    if (newValue !== value) {
      if (!onChange) {
        console.log(`The onChange handler it not set for this checkbox.`, props);
        return;
      }
      onChange(newValue, name);
    }
    if (scrolldown) {
      toggleOptions(false);
    }
  }

  const children = React.Children.map(props.children, child => {
    if ( !child ) return null;
    return React.cloneElement(child, {
      name: child.props.name || name,
      checked: child.props.value === value || child.props.checked,
      onClick: onOptionClick,
      scrolldown,
      isPro,
      disabled
    });
  });

  const filteredChildren = useMemo(() => {
    if (!filterText || !children.length) return children;

    const filtered = [];
    React.Children.forEach(children, child => {
      if (child.props.label.includes(filterText) || child.props.value.includes(filterText)) {
        filtered.push(child);
      }
    });
    return filtered;
  }, [children, filterText]);

  const selectClassNames = useClasses('neko-select', className,{ 
    'show-options': showOptions,
    disabled: disabled || isBusy
  });
  const selectOptionsClassNames = useClasses('neko-select-options', { hidden: !showOptions });
  const selectOptionClassNames = useClasses('neko-select-option', { isBusy });

  return scrolldown ? <Select name={name} {...rest}
        onClick={() => toggleOptions(!showOptions)}
        className={selectClassNames}
        childrenLength={children?.length} data-is-disabled={disabled || isBusy}>
        <SelectedOption className={selectOptionClassNames} ref={componentRef}>
          {isBusy ? <><NekoSpinner type="circle" size="20px" /></> : <>
            {icon && <NekoIcon icon={icon} width={iconSize} height={iconSize} style={{ marginRight: `${Math.max(iconSize - 15, 4)}px` }} />}
            <span className='neko-select-option-label'>{label}</span>
            <div className="rightContent">
              {showUpProOnly && <ProOnly />}
              <Icon icon={showOptions ? chevronUp : chevronDown} width={chevronIconSize} />
            </div>
          </>}
        </SelectedOption>
        {description && <Description>{description}</Description>}

        <NekoContextMenu visible={showOptions} targetRef={componentRef} onClose={onContextMenuClose}>
          <Options className={selectOptionsClassNames}>
            {textFiltering && 
              <OptionsContainer className='neko-select-filter-container'>
                <NekoInput value={filterText} placeholder="Search..." onChange={(v) => setFilterText(v)}
                  onClick={(e) => e.stopPropagation()} style={{ background: `var(--neko-white)` }} inputStyle={{ margin: 0, borderRadius: 0 }} autoFocus />
              </OptionsContainer>
            }
            <OptionsContainer>{[filteredChildren]}</OptionsContainer>
          </Options>
        </NekoContextMenu>

      </Select>
    : children;
};

const StyledNekoOption = props => {
  const { id, name, value, checked, icon, label, description, onClick, scrolldown, isPro = false,
    requirePro = false, disabled, iconSize = 20 } = props;

  const showUpProOnly = requirePro && !isPro;
  const classNames = useClasses({'neko-radio': !scrolldown}, {'neko-select-option': scrolldown}, props.className, { disabled: showUpProOnly || disabled });

  const radioOption = (
    <RadioOption className={classNames} onClick={() => onClick(value, name)}>
      <label htmlFor={id}>
        <Icon icon={checked ? recordCircle : checkboxBlankCircleOutline} width="24px" color={showUpProOnly ? `var(--neko-disabled-form)` : `var(--block-background)`}/>
        <div className="inner-container">
          <span className="label">{label}<ProOnly className="inline" style={{ top: -1 }} show={showUpProOnly} /></span>
          {description ? <small className="description">{description}</small> : null}
        </div>
      </label>
    </RadioOption>
  );

  const selectOption = (
    <SelectOption className={classNames} onClick={() => onClick(value, name)}>
      <div className="option">
        <div className="option-group">
          {icon && (<NekoIcon icon={icon} width={iconSize} height={iconSize}
              style={{ marginRight: `${Math.max(iconSize - 11, 4)}px` }} />
          )}
          {label}
        </div>
        <ProOnly show={showUpProOnly} />
      </div>
    </SelectOption>
  );
  
  return scrolldown ? selectOption : radioOption;
};

/**
 * The NekoSelect has 2 types, radio (default) or select.
 */
const NekoSelect = (props) => {
  return (<StyledNekoSelect {...props} />);
}

NekoSelect.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  onChange: PropTypes.func,
  isBusy: PropTypes.bool,
  chevronIconSize: PropTypes.number,
  textFiltering: PropTypes.bool,
};

NekoSelect.propTypes = {
  name: undefined,
  description: undefined,
  scrolldown: false,
  isPro: false,
  onChange: undefined,
  isBusy: false,
  chevronIconSize: 24,
  textFiltering: undefined,
};

const NekoOption = (props) => {
  return (<StyledNekoOption {...props} />);
}

NekoOption.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  scrolldown: PropTypes.bool,
  isPro: PropTypes.bool,
  requirePro: PropTypes.bool,
  icon: PropTypes.string,
  iconSize: PropTypes.number,
};

NekoOption.defaultProps = {
  id: undefined,
  name: undefined,
  value: undefined,
  checked: false,
  label: undefined,
  description: undefined,
  onClick: undefined,
  scrolldown: false,
  isPro: false,
  requirePro: false,
  icon: undefined,
  iconSize: 20,
};

export { NekoSelect, NekoOption };
